import { useAnalytics } from '@ecosystem/newron-client-lib';
import { elements, feedback, inputs } from '@ecosystem/newron-design-system';
import { GenericCell } from '@components/design/migrated/Table';
import classnames from 'classnames';
import { cloneDeep, noop } from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CategoryLeaderboardPg, ColumnHeaderForCL, GetClassNamesParams, GroupState } from '@models/CategoryLeaderboard';
import { actionCreatorsSideFilters } from '@state/Index';
import {
  displayPrimaryCell,
  displaySecColorForPositiveGrowth,
  displaySecondaryCell,
} from '@utils/hook/tableCellConst/TableCellConst';
import {
  PRODUCT_GROUP,
  TABLE_CLICKED_TRACKING_TITLE,
  TABLE_PRODUCT_GROUP,
  tableClickTrackingFunction,
  TableClickTrackingParam,
} from '@utils/page-analytics/model/TableClickTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { DataType, FilterKeys, Filters, PageName, PageType, SelectedFilters, ShareType } from '@utils/pageConfig/Types';
import * as styles from '@utils/tableViewscss/TableViewBoard.module.scss';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '../utils/CategoryOrSegmentsLeaderboardGroupContext';
import {
  constructProductGroupFilter,
  getBaseFilterOptionsFromResponseConfig,
  updateAppliedCurrencyFilterByCountry,
  updateAppliedFilters,
} from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getGrandTotalValue, modifiedCLTableGrandTotalToTotalValue } from '@utils/TotalUtils';
import CategoryLeaderboardGroupRow from '@components/category-leaderboard/category-leaderboard-table-cell/CategoryLeaderboardGroupRow';
import { categoryLeaderboardResponseConfig } from '@utils/FilterSettings';
import { getRetailPathDefinition, RETAIL_ALLOWED_PATH } from '@utils/UrlUtils';
import { useNavigate } from 'react-router-dom';
import { isAuthorized } from '@utils/Permission';

interface Props {
  headerKey: string;
  categoryLeaderboardPg: CategoryLeaderboardPg;
  isStickyRow?: boolean;
  isCategoryGroup: boolean;
  sortedColumn: string;
  selectedGrowthType: GrowthTypes;
  countries: Filters;
  currencySymbol: string;
  selectedShareType: ShareType;
  isGroupingHeader?: boolean;
  isRowHovered?: boolean;
  groupState?: GroupState;
  isExplorePage?: boolean;
  isPreferenceOwner?: boolean;
}

const CategoryLeaderboardTableCell: React.FC<Props> = ({
  headerKey,
  categoryLeaderboardPg,
  isStickyRow,
  sortedColumn,
  isCategoryGroup,
  selectedGrowthType,
  countries,
  currencySymbol,
  selectedShareType,
  isRowHovered,
  groupState,
  isExplorePage,
  isPreferenceOwner,
}) => {
  const navigate = useNavigate();
  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const { feature } = useSelector((state: IRootState) => state.availabilityPg);
  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { totalSubscriptionPgs, totalAvailablePgs } = useSelector((state: IRootState) => state.availabilityPg);
  const isCategoryFeature = pagesConfig.pageType !== PageType.EXPLORE || feature?.dataType === DataType.CATEGORY;

  const isDisplaySubText = isCategoryFeature;

  const {
    isProductGroupOrSegmentsSelected,
    setIsDeleteModalGroupPopUp,
    setGroupNameToBeDelete,
    isEditGroupMode,
    setIsShareModalGroupPopUp,
  } = useCategoryOrSegmentsLeaderboardGroupEventState();
  const [toggleGroupDown, setToggleGroupDown] = useState<boolean>(false);

  const { Tooltip } = feedback;

  //To display TOTAL instead of GRAND_TOTAL
  const modifiedCategoryLeaderboardPg = modifiedCLTableGrandTotalToTotalValue(categoryLeaderboardPg);

  //NOTE: Remove OR redirection logic, once explore page is live
  const navigateToOpportunityReviewPage = () => {
    const { segmentOrPg } = modifiedCategoryLeaderboardPg;

    const productGroupOptions = getBaseFilterOptionsFromResponseConfig(
      categoryLeaderboardResponseConfig,
      FilterKeys.PRODUCT_GROUP,
    );

    const originalProductGroupOptions = productGroupOptions.find(
      ({ label }) => label === segmentOrPg, // This comparison is case sensitive
    ) ?? {
      value: segmentOrPg,
      label: segmentOrPg,
    };
    const productGroupToUpdate: Filters = constructProductGroupFilter([originalProductGroupOptions]);

    const allFilters: SelectedFilters[] = cloneDeep(pagesConfig.appliedFilters);

    const appliedFiltersOR = allFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters;
    updateAppliedFilters(productGroupToUpdate, appliedFiltersOR);
    updateAppliedFilters(countries, appliedFiltersOR);

    //This part has not been refactored after separating side filters, logic remains
    const appliedFiltersPA = allFilters[PageType.PERFORMANCE_ANALYSIS].selectedFilters;
    updateAppliedFilters(productGroupToUpdate, appliedFiltersPA);
    updateAppliedFilters(countries, appliedFiltersPA);
    updateAppliedCurrencyFilterByCountry(pagesConfig, countries.options[0], appliedFiltersPA);

    // To reset the state in side menu filters with newly updated PG & country selection
    if (pagesConfig.filtersTrayOpened) {
      pagesConfig.toggleFiltersTray(false);
    }

    pagesConfig.setNavigateFromCategoryLeaderboard(true);

    pagesConfig.setSelectedFilters(allFilters);
    pagesConfig.setAppliedFilters(allFilters);
    updateSideFilters({
      appliedFilters: allFilters,
    });

    //Starts table click amplitude tracking
    const trackingParam: TableClickTrackingParam = {
      eventTitle: TABLE_CLICKED_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      track: track,
      componentName: PRODUCT_GROUP,
      elementType: TABLE_PRODUCT_GROUP,
      retailerClientState: retailerClientState,
      pageType: PageType.CATEGORY_LEADERBOARD,
      pageName: retailerClientState.pageViewing,
      optionChosen: modifiedCategoryLeaderboardPg.segmentOrPg,
      access: pagesConfig.access,
    };

    tableClickTrackingFunction(trackingParam);
    //Ends table click amplitude tracking
    navigate(getRetailPathDefinition(RETAIL_ALLOWED_PATH.OPPORTUNITY_REVIEW));
  };

  const applySelectedPGFilter = (event) => {
    event.stopPropagation();

    const { segmentOrPg } = modifiedCategoryLeaderboardPg;

    const productGroupOptions = getBaseFilterOptionsFromResponseConfig(
      categoryLeaderboardResponseConfig,
      FilterKeys.PRODUCT_GROUP,
    );

    const originalProductGroupOptions = productGroupOptions.find(
      ({ label }) => label === segmentOrPg, // This comparison is case sensitive
    ) ?? {
      value: segmentOrPg,
      label: segmentOrPg,
    };
    const productGroupToUpdate: Filters = constructProductGroupFilter([originalProductGroupOptions]);

    const allFilters: SelectedFilters[] = cloneDeep(pagesConfig.appliedFilters);

    const appliedExploreFilters = allFilters[PageType.EXPLORE].selectedFilters;
    const appliedROFilters = allFilters[PageType.RETAILER_OVERVIEW].selectedFilters;
    const appliedORFilters = allFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters;

    updateAppliedFilters(productGroupToUpdate, appliedExploreFilters);
    updateAppliedFilters(productGroupToUpdate, appliedROFilters);
    updateAppliedFilters(productGroupToUpdate, appliedORFilters);

    pagesConfig.setSelectedFilters(allFilters);
    pagesConfig.setAppliedFilters(allFilters);
    updateSideFilters({
      appliedFilters: allFilters,
    });

    //Starts table click amplitude tracking
    const trackingParam: TableClickTrackingParam = {
      eventTitle: TABLE_CLICKED_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      track: track,
      componentName: PRODUCT_GROUP,
      elementType: TABLE_PRODUCT_GROUP,
      retailerClientState: retailerClientState,
      pageType: PageType.CATEGORY_LEADERBOARD,
      pageName: retailerClientState.pageViewing,
      optionChosen: modifiedCategoryLeaderboardPg.segmentOrPg,
      access: pagesConfig.access,
    };

    tableClickTrackingFunction(trackingParam);
    //Ends table click amplitude tracking
  };

  //Handle Group dropdown state
  const handleGroupDropdownToggle = () => {
    const prevState = toggleGroupDown;

    setToggleGroupDown(!prevState);
    groupState?.setIsGroupingRowExpanded?.(!prevState);
  };

  //Handle no checkbox displayed state
  const noCheckbox = (): boolean => {
    return (
      groupState?.isGroupingProductGroup ??
      groupState?.isGroupingHeader ??
      categoryLeaderboardPg.segmentOrPg === getGrandTotalValue()
    );
  };

  //Check if checkbox should be checked
  const isCheckboxCheck = (): boolean => {
    if (
      isEditGroupMode.isEditMode &&
      isEditGroupMode.groupDetails.segmentOrPgValue.includes(modifiedCategoryLeaderboardPg.segmentOrPg)
    ) {
      return true;
    }

    return isProductGroupOrSegmentsSelected(modifiedCategoryLeaderboardPg.segmentOrPg);
  };

  //Check if checkbox should be disabled
  const isCheckboxDisable = (): boolean => {
    return (
      isEditGroupMode.isEditMode &&
      isEditGroupMode.groupDetails.segmentOrPgValue.includes(modifiedCategoryLeaderboardPg.segmentOrPg)
    );
  };

  //Handle to open of the delete modal group state
  const openDeleteModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsDeleteModalGroupPopUp(true);
  };

  const openShareModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsShareModalGroupPopUp(true);
  };

  const getClassNames = ({
    baseClasses = [],
    isSticky = false,
    sortColumn = null,
    keyHeader = null,
    additionalClasses = [],
  }: GetClassNamesParams) => {
    const conditionalClasses: { [key: string]: boolean } = {
      [styles.TableViewboard__StickyRow]: isSticky,
    };

    // Apply the sorted cell class only if both sortColumn and keyHeader are defined
    if (sortColumn !== null && keyHeader !== null) {
      conditionalClasses[styles.TableViewboard__SortedCell] = sortColumn === keyHeader;
    }

    return classnames(...baseClasses, conditionalClasses, ...additionalClasses);
  };

  //To display checkbox
  if (ColumnHeaderForCL.checkbox === headerKey) {
    return (
      <td
        className={getClassNames({
          baseClasses: [styles.TableViewboard__TableCell],
          isSticky: isStickyRow,
        })}
      >
        {!noCheckbox() && (
          <inputs.Checkbox
            disabled={isCheckboxDisable()}
            state={isCheckboxCheck() ? inputs.CheckboxState.CHECKED : inputs.CheckboxState.UNCHECKED}
            onChange={noop}
            onClick={noop}
          ></inputs.Checkbox>
        )}
      </td>
    );
  }

  const getSubTextAvailabilityPg = () => {
    return isDisplaySubText ? (
      <p className="text-lighter">
        {totalAvailablePgs}/{totalSubscriptionPgs} Category loaded
      </p>
    ) : null;
  };

  const renderProductGroupOrSegment = () => {
    const renderCountry = !isExplorePage ? <p>{countries.options[0]?.label}</p> : null;

    const isAuthorizedRetailKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);

    //NOTE: Remove KAD user check, once Explore page is live.
    if (!isCategoryFeature || (isAuthorizedRetailKadUser && pagesConfig.pageName === PageName.CATEGORY_LEADERBOARD)) {
      return (
        <div>
          <p>{modifiedCategoryLeaderboardPg.segmentOrPg}</p>
          {renderCountry}
        </div>
      );
    }

    return (
      <button
        tabIndex={0}
        onClick={
          pagesConfig.pageName === PageName.CATEGORY_LEADERBOARD
            ? navigateToOpportunityReviewPage
            : applySelectedPGFilter
        }
        onKeyDown={
          pagesConfig.pageName === PageName.CATEGORY_LEADERBOARD
            ? navigateToOpportunityReviewPage
            : applySelectedPGFilter
        }
        className={styles.TableViewboard__TableCell_CategoryGroup_title}
      >
        <p>{modifiedCategoryLeaderboardPg.segmentOrPg}</p>
        {renderCountry}
      </button>
    );
  };

  if (isCategoryGroup) {
    //To display for group rows
    if (groupState?.isGroupingHeader) {
      return (
        <CategoryLeaderboardGroupRow
          groupState={groupState}
          isRowHovered={isRowHovered}
          toggleGroupDown={toggleGroupDown}
          handleGroupDropdownToggle={handleGroupDropdownToggle}
          openDeleteModalGroup={openDeleteModalGroup}
          segmentOrPg={modifiedCategoryLeaderboardPg.segmentOrPg}
          isEditGroupMode={isEditGroupMode}
          openShareModalGroup={openShareModalGroup}
        />
      );
    }

    if (!groupState?.isGroupingHeader) {
      return (
        <td
          key={modifiedCategoryLeaderboardPg.segmentOrPg}
          className={getClassNames({
            baseClasses: [styles.TableViewboard__TableCell, styles.TableViewboard__TableCell_Total],
            isSticky: isStickyRow,
          })}
        >
          {isStickyRow ? (
            <div>
              <p>{modifiedCategoryLeaderboardPg.segmentOrPg}</p>
              {getSubTextAvailabilityPg()}
            </div>
          ) : (
            <div
              className={classnames([
                styles.TableViewboard__TableCell__grouping,
                styles.TableViewboard__TableCell__grouping__pgsWithDeleteButton,
              ])}
            >
              {renderProductGroupOrSegment()}
              {groupState?.isGroupingProductGroup && isRowHovered && (
                <Tooltip placement="top">
                  <Tooltip.Content>
                    <p>Remove product group from group</p>
                  </Tooltip.Content>
                  {isPreferenceOwner && (
                    <button
                      type="button"
                      aria-label="Remove product group from group"
                      onClick={() => groupState?.onDeleteGroupPgs?.(modifiedCategoryLeaderboardPg.segmentOrPg)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="icon-button brand"
                    >
                      <elements.Icon color="brand">
                        <elements.Icon.icons.RemoveOutline />
                      </elements.Icon>
                    </button>
                  )}
                </Tooltip>
              )}
            </div>
          )}
        </td>
      );
    }
  }

  return (
    <td
      className={getClassNames({
        baseClasses: [styles.TableViewboard__TableCell],
        isSticky: isStickyRow,
        sortColumn: sortedColumn,
        keyHeader: headerKey,
      })}
    >
      {groupState?.isGroupEmpty ? (
        <GenericCell primary={'-'} />
      ) : (
        <GenericCell
          primary={displayPrimaryCell(
            headerKey,
            modifiedCategoryLeaderboardPg,
            currencySymbol,
            true,
            selectedShareType,
          )}
          secondary={displaySecondaryCell(
            headerKey,
            modifiedCategoryLeaderboardPg,
            selectedGrowthType,
            currencySymbol,
            true,
            selectedShareType,
          )}
          secondaryColor={displaySecColorForPositiveGrowth(
            headerKey,
            modifiedCategoryLeaderboardPg,
            selectedGrowthType,
            true,
            selectedShareType,
          )}
        />
      )}
    </td>
  );
};

export default CategoryLeaderboardTableCell;
